import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Button,
    Checkbox,
    ListItemText,
    CircularProgress,
    Switch,
    Tooltip,
    TextField,
    ListSubheader,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
    cleanCustomerId,
    customerbaseddevices,
    customerbasedshift
} from '../../Services/app/companyservice';
import { telemetrykeydata } from '../../Services/app/operatorservice';
import { getKpiMetricByPeriod } from '../../Services/app/analyticsservice';
import { startTokenAutoRefresh } from '../../Services/app/loginservice';
import { useMachineGroups } from '../../Shared/hooks/useMachineGroups';
import PlantFilterSelect from '../../Shared/components/PlantFilterSelect';
import { useNavigate } from 'react-router-dom';
import {
    timeToSeconds,
    getShiftTimes as getShiftTimesUtil,
    filterByShift,
    getLatestDataPoint as getLatestDataPointUtil,
    getBreakWindowsForShift,
    computeBreakDurations,
    makeShiftExclusionChecker,
    parseManualHolidayAttr,
    buildMergedHolidaySetFromAttr,
    makeMachineOverrideCheckers,
} from '../../Shared/utils/holidaycalculation';

export default function OnePageDashboard() {
    const customerId = localStorage.getItem('CustomerID');
    // Keep the tenant token in state so the page re-renders (and rebuilds the iframe URL)
    // when token1 is written/refreshed in localStorage — otherwise a fresh login that sets
    // token1 asynchronously leaves the embed pointing at ThingsBoard's default dashboard.
    const [token, setToken] = useState(localStorage.getItem('token1') || '');

    // Landing here directly (bookmark/reload) skips Login.js, so nothing is refreshing
    // token1 in the background — it just sits in localStorage until it expires, after
    // which the Grafana embed silently falls back to ThingsBoard's default dashboard.
    // Restart the refresh loop here too so token1 stays valid for the embed.
    useEffect(() => {
        startTokenAutoRefresh();
    }, []);

    useEffect(() => {
        const syncToken = () => {
            const latest = localStorage.getItem('token1') || '';
            setToken(prev => (prev !== latest ? latest : prev));
        };
        window.addEventListener('storage', syncToken); // updated in another tab
        const interval = setInterval(syncToken, 5000);  // updated in same tab by refresh API
        return () => {
            window.removeEventListener('storage', syncToken);
            clearInterval(interval);
        };
    }, []);

    const [devices, setDevices] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('all');
    const [selectedShift, setSelectedShift] = useState(null);
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const [mainGrafanaUrl, setMainGrafanaUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isBeforeFirstShift, setIsBeforeFirstShift] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [machineDropdownSearch, setMachineDropdownSearch] = useState('');
    const isInitialLoad = useRef(true);
    // Sequences overlapping submits (manual click + 5-min auto-refresh) so a slower,
    // older request can't overwrite the URL set by a newer one.
    const requestIdRef = useRef(0);

    const manualOverridesRef = useRef(parseManualHolidayAttr(null, []));
    const fetchManualOverrides = useCallback(async () => {
        if (!customerId) return manualOverridesRef.current;
        try {
            const res = await customerbasedshift(customerId, 'manual_holiday');
            manualOverridesRef.current = parseManualHolidayAttr(res[0]?.value, selectedMachines);
        } catch {
            /* keep last known overrides on failure */
        }
        return manualOverridesRef.current;
    }, [customerId]);
    const {
        // devices,
        deviceNameID,
        machineGroups,
        availableMachines,
        selectedMachines,
        selectedGroups,
        showMachineGroupsDropdown,
        isAllMachinesSelected,
        handleGroupChange,
        handleMachineChange,
        getDeviceObjectsForMachines,
        plantOptions,
        selectedPlants,
        showPlantFilter,
        handlePlantChange,
    } = useMachineGroups(customerId);
    const navigate = useNavigate();

    useEffect(() => {
        const handler = (event) => {
            if (event.data?.type === "NAVIGATE") {
                const params = new URLSearchParams();
                const status = event.data.status;
                const machines = event.data.machines;
                if (status && status !== "All") params.set("status", status);
                if (Array.isArray(machines) && machines.length > 0) params.set("machines", machines.join(","));
                const query = params.toString();
                navigate(query ? `${event.data.path}?${query}` : event.data.path);
            }
        };

        window.addEventListener("message", handler);

        return () => window.removeEventListener("message", handler);
    }, [navigate]);

    const [dashboardData, setDashboardData] = useState({
        metrics: { oee: 0 },
        durations: {
            total_run_duration: 0,
            total_idle_duration: 0,
            total_alarm_duration: 0,
            total_disconnect_duration: 0,
            total_settings_duration: 0
        },
        machinePerformance: {
            bestMachines: [],
            worstMachines: [],
            allMachines: [],
            highestOee: 0,
            lowestOee: 0
        },
        machinesWithZeroRunDuration: []
    });

    const getCurrentShift = useCallback((shifts) => {
        if (!Array.isArray(shifts) || shifts.length === 0) return "allshift";
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        for (const s of shifts) {
            const [fromH, fromM] = s.start_time.split(":").map(Number);
            const [toH, toM] = s.end_time.split(":").map(Number);
            const fromMinutes = fromH * 60 + fromM;
            const toMinutes = toH * 60 + toM;
            if (
                (fromMinutes <= currentMinutes && currentMinutes < toMinutes) ||
                (fromMinutes > toMinutes && (currentMinutes >= fromMinutes || currentMinutes < toMinutes))
            ) {
                return String(s.shift_no);
            }
        }
        return "allshift";
    }, []);

    const getShiftTimes = useCallback(
        (shiftList, shiftNo, date) => getShiftTimesUtil(shiftList, shiftNo, date),
        []
    );    // Memoized shift times calculation (delegates to shared holiday util)


    const getShiftTimeRanges = useCallback((shifts, shiftNo, date) => {
        if (shiftNo === "allshift") {
            return shifts.map(shift => ({
                shiftNo: String(shift.shift_no),
                ...getShiftTimes(shifts, String(shift.shift_no), date)
            }));
        }
        return [{
            shiftNo: String(shiftNo),
            ...getShiftTimes(shifts, String(shiftNo), date)
        }];
    }, [getShiftTimes]);

    const fetchShifts = useCallback(async () => {
        if (!customerId) return;

        try {
            const result = await customerbasedshift(customerId, 'allShift');
            const shiftList = result[0]?.value || [];
            setShifts(shiftList);
        } catch (err) {
            console.error('Failed to fetch shifts', err);
            setShifts([]);
        }
    }, [customerId]);

    const fetchDevices = useCallback(async () => {
        if (!customerId) return;

        try {
            const result = await customerbaseddevices(customerId, 100, 0);
            setDevices(result.data || []);
        } catch (err) {
            console.error("Failed to fetch devices", err);
            setDevices([]);
        }
    }, [customerId]);

    const fetchTelemetryData = useCallback(async (entityId, entityType, keys, from, to) => {
        try {
            return await telemetrykeydata(entityId, entityType, keys, from, to);
        } catch (error) {
            console.error(`Error fetching telemetry data for ${entityType}:${entityId}`, error);
            return null;
        }
    }, []);

    const getLatestDataPoint = useCallback((dataArray) => getLatestDataPointUtil(dataArray), []);

    const fetchAllDashboardData = useCallback(async () => {
        if (!shifts.length || selectedShift === null || !customerId || !startDate || !endDate) return;

        // Build all (day × shift) time ranges across the date range
        const allRanges = [];
        let currentDay = startDate.startOf('day');
        const lastDay = endDate.startOf('day');
        while (!currentDay.isAfter(lastDay)) {
            const dayRanges = getShiftTimeRanges(shifts, selectedShift, currentDay);
            allRanges.push(...dayRanges.filter(r => r.from && r.to));
            currentDay = currentDay.add(1, 'day');
        }
        if (allRanges.length === 0) return;

        // Build full-day ranges (all shifts) for holiday detection — always needed regardless of selectedShift
        const allRangesAllShiftsForFetch = [];
        {
            let d = startDate.startOf('day');
            const last = endDate.startOf('day');
            while (!d.isAfter(last)) {
                const dayRanges = getShiftTimeRanges(shifts, "allshift", d);
                allRangesAllShiftsForFetch.push(...dayRanges.filter(r => r.from && r.to));
                d = d.add(1, 'day');
            }
        }

        const overallFrom = allRangesAllShiftsForFetch.length > 0
            ? allRangesAllShiftsForFetch[0].from
            : allRanges[0].from;
        const overallTo = allRangesAllShiftsForFetch.length > 0
            ? allRangesAllShiftsForFetch[allRangesAllShiftsForFetch.length - 1].to
            : allRanges[allRanges.length - 1].to;

        const telemetryKeys = [
            "oee",
            "total_duration"
        ];

        const selectedDeviceIds = deviceNameID
            .filter((d) => selectedMachines.includes(d.name))
            .map((d) => d.id);

        console.log(deviceNameID, "device name list");
        console.log(selectedMachines, "calling apis for");

        const machineDataMap = new Map();
        const allMachineDataMap = new Map();

        await Promise.all(
            selectedDeviceIds.map(async (deviceId) => {
                const data = await fetchTelemetryData(deviceId, "DEVICE", telemetryKeys, overallFrom, overallTo);
                machineDataMap.set(deviceId, data);
                allMachineDataMap.set(deviceId, data); // reuse, already fetched
            })
        );

        const promises = [];

        promises.push(
            (async () => {
                const metrics = { oee: 0 };
                const allDeviceResults = [];

                for (const deviceId of selectedDeviceIds) {
                    const values = { oee: [] };
                    const rawData = machineDataMap.get(deviceId);

                    for (const shiftRange of allRanges) {
                        const data = filterByShift(rawData, shiftRange.from, shiftRange.to);
                        const latest = getLatestDataPoint(data?.oee);
                        if (latest) {
                            const val = parseFloat(latest.value);
                            if (!isNaN(val) && val > 0) values.oee.push(val);
                        }
                    }

                    const deviceMetrics = {
                        oee: values.oee.length > 0
                            ? Math.round(values.oee.reduce((a, b) => a + b, 0) / values.oee.length)
                            : 0
                    };
                    allDeviceResults.push(deviceMetrics);
                }

                if (allDeviceResults.length > 0) {
                    const validDevices = allDeviceResults.filter((m) => m.oee > 0);
                    if (validDevices.length > 0) {
                        const sum = validDevices.reduce((acc, m) => acc + m.oee, 0);
                        metrics.oee = Math.round(sum / validDevices.length);
                    }
                }

                console.log("✅ Final averaged metrics:", metrics);
                return { metrics };
            })()
        );

        promises.push(
            (async () => {
                const totalDurations = {
                    total_run_duration: 0,
                    total_idle_duration: 0,
                    total_alarm_duration: 0,
                    total_disconnect_duration: 0,
                    total_settings_duration: 0
                };

                const machineTotalRunDuration = new Map();
                const machineShiftZeroRuntimes = new Map(); // Track machine-shift combos with zero runtime
                const machineAlarmIdleDurations = new Map(); // Track machine-date alarm/idle durations

                for (const deviceId of selectedDeviceIds) {
                    let totalRunForMachine = 0;
                    const rawData = machineDataMap.get(deviceId);
                    const shiftZeros = [];
                    const dateAlarmIdleMap = new Map(); // { date: { alarmSum, idleSum } }

                    for (const shiftRange of allRanges) {
                        const data = filterByShift(rawData, shiftRange.from, shiftRange.to);

                        // Get latest data point for this shift-date range
                        const latestDataPoint = getLatestDataPoint(data?.total_duration);
                        const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];

                        try {
                            const parsed = JSON.parse(latestDataPoint.value);
                            Object.keys(totalDurations).forEach((key) => {
                                totalDurations[key] += parsed[key] || 0;
                            });

                            const runDur = parsed.total_run_duration;
                            const alarmDur = parsed.total_alarm_duration || 0;
                            const idleDur = parsed.total_idle_duration || 0;
                            const disconnectDur = parsed.total_disconnect_duration || 0;

                            totalRunForMachine += runDur || 0;

                            // Track alarm, idle, and disconnect durations by date
                            if (!dateAlarmIdleMap.has(shiftDate)) {
                                dateAlarmIdleMap.set(shiftDate, { alarmSum: 0, idleSum: 0, disconnectSum: 0 });
                            }
                            const dateData = dateAlarmIdleMap.get(shiftDate);
                            dateData.alarmSum += alarmDur;
                            dateData.idleSum += idleDur;
                            dateData.disconnectSum += disconnectDur;


                        } catch (err) {
                            console.error(`Error parsing total_duration for ${deviceId}`, err);
                        }
                    }

                    machineTotalRunDuration.set(deviceId, totalRunForMachine);
                    machineShiftZeroRuntimes.set(deviceId, shiftZeros);
                    machineAlarmIdleDurations.set(deviceId, dateAlarmIdleMap);
                    console.log(`📊 Device: ${deviceId}, Total Run Duration: ${totalRunForMachine}`);
                }

                // Track per-date and per-shift-date durations for each machine. The
                // per-shift-date maps let manual holidays remove ONLY the marked
                // machine's data for the marked shifts/dates (see new_total_* recompute below).
                const machineRunByDate = new Map();      // `${deviceId}-${date}` → secs
                const machineRunByShiftDate = new Map(); // `${deviceId}-${shiftNo}-${date}` → secs
                const machineIdleByShiftDate = new Map();
                const machineAlarmByShiftDate = new Map();
                const machineDiscByShiftDate = new Map();

                for (const deviceId of selectedDeviceIds) {
                    const rawData = machineDataMap.get(deviceId);
                    for (const shiftRange of allRanges) {
                        const data = filterByShift(rawData, shiftRange.from, shiftRange.to);
                        const latestDataPoint = getLatestDataPoint(data?.total_duration);
                        const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];

                        if (latestDataPoint?.value) {
                            try {
                                const parsed = JSON.parse(latestDataPoint.value);
                                const runDur = parsed.total_run_duration || 0;
                                const dayKey = `${deviceId}-${shiftDate}`;
                                const shiftKey = `${deviceId}-${shiftRange.shiftNo}-${shiftDate}`;
                                machineRunByDate.set(dayKey, (machineRunByDate.get(dayKey) || 0) + runDur);
                                machineRunByShiftDate.set(shiftKey, (machineRunByShiftDate.get(shiftKey) || 0) + runDur);
                                machineIdleByShiftDate.set(shiftKey, (machineIdleByShiftDate.get(shiftKey) || 0) + (parsed.total_idle_duration || 0));
                                machineAlarmByShiftDate.set(shiftKey, (machineAlarmByShiftDate.get(shiftKey) || 0) + (parsed.total_alarm_duration || 0));
                                machineDiscByShiftDate.set(shiftKey, (machineDiscByShiftDate.get(shiftKey) || 0) + (parsed.total_disconnect_duration || 0));
                            } catch (err) { /* ignore */ }
                        }
                    }
                }

                const TWENTY_HOURS_SECONDS = 20 * 3600; // 72000 seconds
                const machinesWithZeroRunDuration = [];
                const dateRunExceeded = new Map(); // Track machines excluded for specific dates

                // Use the pre-built full-day ranges (all shifts) for holiday detection
                const allRangesAllShifts = allRangesAllShiftsForFetch;

                // Step 1: Collect all unique dates from full-day ranges
                const allDatesInRange = new Set();
                for (const shiftRange of allRangesAllShifts) {
                    const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];
                    allDatesInRange.add(shiftDate);
                }

                // Build full-day run/alarm/idle maps using ALL machines + ALL shifts for holiday detection
                const fullDayRunByDate = new Map(); // { deviceId-date: runDuration }
                const fullDayAlarmIdleByDevice = new Map(); // { deviceId: Map<date, {alarmSum,idleSum,disconnectSum}> }

                for (const deviceId of selectedDeviceIds) {
                    const rawData = allMachineDataMap.get(deviceId);
                    const dateAlarmIdleMap = new Map();

                    for (const shiftRange of allRangesAllShifts) {
                        const data = filterByShift(rawData, shiftRange.from, shiftRange.to);
                        const latestDataPoint = getLatestDataPoint(data?.total_duration);
                        const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];

                        if (latestDataPoint?.value) {
                            try {
                                const parsed = JSON.parse(latestDataPoint.value);
                                const runDur = parsed.total_run_duration || 0;
                                const alarmDur = parsed.total_alarm_duration || 0;
                                const idleDur = parsed.total_idle_duration || 0;
                                const disconnectDur = parsed.total_disconnect_duration || 0;

                                const runKey = `${deviceId}-${shiftDate}`;
                                fullDayRunByDate.set(runKey, (fullDayRunByDate.get(runKey) || 0) + runDur);

                                if (!dateAlarmIdleMap.has(shiftDate)) {
                                    dateAlarmIdleMap.set(shiftDate, { alarmSum: 0, idleSum: 0, disconnectSum: 0 });
                                }
                                const dd = dateAlarmIdleMap.get(shiftDate);
                                dd.alarmSum += alarmDur;
                                dd.idleSum += idleDur;
                                dd.disconnectSum += disconnectDur;
                            } catch (err) { /* ignore parse errors */ }
                        }
                    }
                    fullDayAlarmIdleByDevice.set(deviceId, dateAlarmIdleMap);
                }

                // Step 1: Calculate TOTAL run duration per date across ALL machines (full day)
                const totalRunDurationByDate = new Map();
                for (const date of allDatesInRange) {
                    let totalRun = 0;
                    for (const deviceId of selectedDeviceIds) {
                        const runKey = `${deviceId}-${date}`;
                        totalRun += fullDayRunByDate.get(runKey) || 0;
                    }
                    // Always record the date (even if 0) so zero-data days are included in condition check
                    totalRunDurationByDate.set(date, totalRun);
                }

                // Step 1b: Calculate TOTAL expected shift time per date using ALL shifts × ALL machines.
                // For today: only count shifts from the first shift start up to the current shift end
                // (skip future shifts that haven't started yet) so a partially-elapsed day isn't
                const todayDateStr = new Date().toISOString().split('T')[0];
                const nowMs = Date.now();

                const expectedShiftTimeByDate = new Map();
                for (const shiftRange of allRangesAllShifts) {
                    const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];

                    // For today: skip any shift whose start time is still in the future
                    if (shiftDate === todayDateStr && shiftRange.from > nowMs) continue;

                    const rawShiftDurationSecs = Math.floor((shiftRange.to - shiftRange.from) / 1000);

                    const shiftNo = shiftRange.shiftNo;
                    const shiftData = shifts.find(s => String(s.shift_no) === String(shiftNo));
                    const breakTimeSecs = shiftData ? Math.floor(timeToSeconds(shiftData.break_time || "00:00:00")) : 0;
                    const shiftDurationWithoutBreak = Math.max(0, rawShiftDurationSecs - breakTimeSecs);

                    if (!expectedShiftTimeByDate.has(shiftDate)) {
                        expectedShiftTimeByDate.set(shiftDate, 0);
                    }
                    expectedShiftTimeByDate.set(
                        shiftDate,
                        expectedShiftTimeByDate.get(shiftDate) + (shiftDurationWithoutBreak * selectedDeviceIds.length)
                    );
                }

                const totalAlarmByDate = new Map();
                const totalIdleByDate = new Map();
                const totalDisconnectByDate = new Map();

                for (const deviceId of selectedDeviceIds) {
                    const dateAlarmIdle = fullDayAlarmIdleByDevice.get(deviceId);
                    if (!dateAlarmIdle) continue;

                    for (const [date, { alarmSum, idleSum, disconnectSum }] of dateAlarmIdle) {
                        if (!totalAlarmByDate.has(date)) totalAlarmByDate.set(date, 0);
                        totalAlarmByDate.set(date, totalAlarmByDate.get(date) + alarmSum);

                        if (!totalIdleByDate.has(date)) totalIdleByDate.set(date, 0);
                        totalIdleByDate.set(date, totalIdleByDate.get(date) + idleSum);

                        if (!totalDisconnectByDate.has(date)) totalDisconnectByDate.set(date, 0);
                        totalDisconnectByDate.set(date, totalDisconnectByDate.get(date) + disconnectSum);
                    }
                }

                // Step 3: Check condition for each date
                // Condition: run % <= 20% AND (alarm + idle + disconnect) > 20h
                // Always check every date in the selected range — including days where
                // all telemetry values are 0 or null (no data = no production = exclude).
                const datesToCheck = new Set(allDatesInRange);

                const conditionCheckResults = [];
                const datesExcluded = [];
                const sortedDates = Array.from(datesToCheck).sort();

                for (const date of sortedDates) {
                    const totalRunForDate = totalRunDurationByDate.get(date) || 0;
                    const expectedTimeForDate = expectedShiftTimeByDate.get(date) || 1;
                    const totalAlarm = totalAlarmByDate.get(date) || 0;
                    const totalIdle = totalIdleByDate.get(date) || 0;
                    const totalDisconnect = totalDisconnectByDate.get(date) || 0;

                    const runPercentage = (totalRunForDate / expectedTimeForDate) * 100;

                    const runConditionMet = runPercentage <= 20;
                    const summedAlarmIdleDisconnect = totalAlarm + totalIdle + totalDisconnect;
                    const alarmConditionMet = summedAlarmIdleDisconnect > TWENTY_HOURS_SECONDS;

                    const bothAreZero = totalRunForDate === 0 && summedAlarmIdleDisconnect === 0;
                    const conditionMet = (runConditionMet && alarmConditionMet) || bothAreZero;

                    conditionCheckResults.push({
                        Date: date,
                        'Run %': runPercentage.toFixed(2),
                        'Run Hrs': (totalRunForDate / 3600).toFixed(2),
                        'Alarm Hrs': (totalAlarm / 3600).toFixed(2),
                        'Idle Hrs': (totalIdle / 3600).toFixed(2),
                        'Disc Hrs': (totalDisconnect / 3600).toFixed(2),
                        'Sum Hrs': (summedAlarmIdleDisconnect / 3600).toFixed(2),
                        'Result': conditionMet ? '🔴 EXCLUDED' : '✅ INCLUDED'
                    });

                    if (conditionMet) {
                        datesExcluded.push(date);
                        for (const deviceId of selectedDeviceIds) {
                            if (!dateRunExceeded.has(deviceId)) {
                                dateRunExceeded.set(deviceId, []);
                            }
                            if (!dateRunExceeded.get(deviceId).includes(date)) {
                                dateRunExceeded.get(deviceId).push(date);
                            }
                        }
                    }
                }

                const fullyExcludedMachines = [];
                machinesWithZeroRunDuration.push(...fullyExcludedMachines);

                const manualOverrides = manualOverridesRef.current;
                const { isHoliday: isMachineHoliday, isProduction: isMachineProduction } =
                    makeMachineOverrideCheckers(manualOverrides);
                const autoExcludedSet = new Set(datesExcluded);

                const idToName = (deviceId) => deviceNameID.find(d => d.id === deviceId)?.name || deviceId;
                const isExcludedForData = (machineName, shiftNo, dateStr) => {
                    if (isMachineProduction(machineName, shiftNo, dateStr)) return false;
                    if (isMachineHoliday(machineName, shiftNo, dateStr)) return true;
                    if (autoExcludedSet.has(dateStr)) return true;
                    return false;
                };

                // Recompute run/idle/alarm/disconnect from the per-machine, per-shift, per-date
               let inclRun = 0, inclIdle = 0, inclAlarm = 0, inclDisc = 0;
                for (const deviceId of selectedDeviceIds) {
                    const machineName = idToName(deviceId);
                    for (const shiftRange of allRanges) {
                        const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];
                        if (isExcludedForData(machineName, shiftRange.shiftNo, shiftDate)) continue;
                        const k = `${deviceId}-${shiftRange.shiftNo}-${shiftDate}`;
                        inclRun += machineRunByShiftDate.get(k) || 0;
                        inclIdle += machineIdleByShiftDate.get(k) || 0;
                        inclAlarm += machineAlarmByShiftDate.get(k) || 0;
                        inclDisc += machineDiscByShiftDate.get(k) || 0;
                    }
                }
                const new_total_run_duration = Math.max(0, Math.round(inclRun));
                const new_total_idle_duration = Math.max(0, Math.round(inclIdle));
                const new_total_alarm_duration = Math.max(0, Math.round(inclAlarm));
                const new_total_disconnect_duration = Math.max(0, Math.round(inclDisc));

                /* --------------------------------------------------------- */
                /* Break-time durations: the run/idle/alarm/disconnect that   */
                /* occurred inside each shift's break windows, summed across   */
                /* every selected machine, shift and date.                     */
                /* --------------------------------------------------------- */
                const breakDurations = { break_run: 0, break_idle: 0, break_alarm: 0, break_disconnect: 0 };
                for (const deviceId of selectedDeviceIds) {
                    const rawData = machineDataMap.get(deviceId);
                    const machineName = idToName(deviceId);
                    for (const shiftRange of allRanges) {
                        // Skip holiday-excluded shift-dates (same priority rule as parts/new_total_*)
                        const shiftDate = new Date(shiftRange.from).toISOString().split('T')[0];
                        if (isExcludedForData(machineName, shiftRange.shiftNo, shiftDate)) continue;

                        const shiftData = shifts.find((s) => String(s.shift_no) === String(shiftRange.shiftNo));
                        if (!shiftData) continue;
                        const breakWindows = getBreakWindowsForShift(shiftData, shiftRange.from, shiftRange.to);
                        if (!breakWindows.length) continue;
                        const dvBreaks = computeBreakDurations(rawData, shiftRange.from, breakWindows);
                        breakDurations.break_run += dvBreaks.break_run;
                        breakDurations.break_idle += dvBreaks.break_idle;
                        breakDurations.break_alarm += dvBreaks.break_alarm;
                        breakDurations.break_disconnect += dvBreaks.break_disconnect;
                    }
                }
                // Round to whole seconds (values accumulate from ms → seconds conversion)
                breakDurations.break_run = Math.round(breakDurations.break_run);
                breakDurations.break_idle = Math.round(breakDurations.break_idle);
                breakDurations.break_alarm = Math.round(breakDurations.break_alarm);
                breakDurations.break_disconnect = Math.round(breakDurations.break_disconnect);
                console.log('✅ Break-time durations:', breakDurations);

                return {
                    durations: { ...totalDurations, new_total_run_duration, new_total_idle_duration, new_total_alarm_duration, new_total_disconnect_duration, ...breakDurations },
                    machinesWithZeroRunDuration,
                    machineShiftZeroRuntimes,
                    machineAlarmIdleDurations,
                    dateRunExceeded,
                    machineRunByDate
                };
            })()
        );

        promises.push(
            (async () => {
                const selectedMachinesObj = getDeviceObjectsForMachines(selectedMachines);
                const machinePromises = selectedMachinesObj.map(async (machine) => {
                    const oeeValues = [];
                    const rawData = machineDataMap.get(machine.id.id);

                    for (const shiftRange of allRanges) {
                        const data = filterByShift(rawData, shiftRange.from, shiftRange.to);
                        const latest = getLatestDataPoint(data?.oee);
                        if (latest) {
                            const val = parseFloat(latest.value);
                            if (!isNaN(val) && val > 0) oeeValues.push(val);
                        }
                    }

                    const machineOee =
                        oeeValues.length > 0
                            ? Math.round(oeeValues.reduce((a, b) => a + b, 0) / oeeValues.length)
                            : 0;

                    return { machineId: machine.id.id, machineName: machine.name, oee: machineOee };
                });

                const allMachineData = await Promise.all(machinePromises);
                const validMachines = allMachineData.filter((m) => m.oee > 0);
                const sorted = [...validMachines].sort((a, b) => b.oee - a.oee);

                let bestMachines, worstMachines;

                if (validMachines.length === 1) {
                    bestMachines = sorted;
                    const machineWithoutData = allMachineData.find((m) => m.oee === 0);
                    worstMachines = machineWithoutData ? [{ ...machineWithoutData, oee: 0 }] : [{ ...sorted[0], oee: 0 }];
                } else {
                    bestMachines = sorted.filter((m) => m.oee === sorted[0]?.oee);
                    worstMachines = sorted.filter((m) => m.oee === sorted.at(-1)?.oee);
                }

                return {
                    machinePerformance: {
                        allMachines: sorted,
                        bestMachines,
                        worstMachines,
                        highestOee: sorted[0]?.oee || 0,
                        lowestOee: sorted.at(-1)?.oee || 0
                    }
                };
            })()
        );

        promises.push(
            (async () => {
                try {
                    const machineParamForMetric = selectedMachines.join(",");
                    const shiftParamForMetric = selectedShift === "allshift"
                        ? shifts.map(s => s.shift_no).join(",")
                        : String(selectedShift);
                    const fromDateStr = startDate.format('YYYY-MM-DD');
                    const toDateStr = endDate.format('YYYY-MM-DD');
                    const metricByPeriod = await getKpiMetricByPeriod(machineParamForMetric, shiftParamForMetric, fromDateStr, toDateStr);
                    return { metricByPeriod };
                } catch (err) {
                    console.error('Error fetching metric by period', err);
                    return { metricByPeriod: null };
                }
            })()
        );

        try {
            const results = await Promise.all(promises);
            const combined = results.reduce((acc, r) => ({ ...acc, ...r }), {});

            setDashboardData((prev) => ({ ...prev, ...combined }));
            return combined;
        } catch (err) {
            console.error("Error fetching dashboard data", err);
            return null;
        }
    }, [
        shifts,
        selectedShift,
        startDate,
        endDate,
        customerId,
        selectedDevice,
        devices,
        fetchTelemetryData,
        getShiftTimes,
        getShiftTimeRanges,
        selectedMachines
    ]);


    const generateGrafanaUrls = useCallback((data) => {
        if (!customerId || !selectedShift) return { mainUrl: '' };
        const machineParam = selectedMachines.join(",");
        const cleanedId = cleanCustomerId(customerId);

        const manualOverrides = manualOverridesRef.current;
        const manualHolidaySet = buildMergedHolidaySetFromAttr(manualOverrides, customerId, selectedShift);
        const manualProductionSet = manualOverrides.manualProductionDays;

        // Per-shift manual overrides for duration calculation
        const gPerShiftHol = manualOverrides.perShiftHol;
        const gPerShiftProd = manualOverrides.perShiftProd;

        // Priority: per-shift production > whole-day production > per-shift holiday > whole-day holiday
        const isShiftExcludedForDuration = makeShiftExclusionChecker({
            perShiftHol: gPerShiftHol,
            perShiftProd: gPerShiftProd,
            wholeDayProd: manualProductionSet,
            wholeDayHol: manualHolidaySet,
        });

        // Machine-aware manual overrides: a manual holiday removes ONLY the marked
        // machine's shift/date from BOTH duration1 (target) and duration2 (actual).
        const { isHoliday: isMachineHolidayDur, isProduction: isMachineProductionDur } =
            makeMachineOverrideCheckers(manualOverrides);
        const gIdToName = (deviceId) => deviceNameID.find(d => d.id === deviceId)?.name || deviceId;

        // Read the tenant token fresh at build time — `token` captured at mount can be
        // stale/empty right after login (the tenant login writes token1 asynchronously),
        // which makes the embedded ThingsBoard fall back to its default dashboard.
        const liveToken = localStorage.getItem('token1') || token;
        const bearerToken = encodeURIComponent(`Bearer+${liveToken}`);
        const entityType = 'CUSTOMER';
        const isAllMachinesSelected = selectedDevice === 'all';
        const entityId = cleanedId;

        const shiftArray = selectedShift === "allshift"
            ? shifts.map(s => String(s.shift_no))
            : [String(selectedShift)];

        const shiftVar = `[${shiftArray.join(',')}]`;

        const oeeVar = data.metrics.oee || 0;

        const from = startDate.startOf('day').valueOf();
        const to = endDate.endOf('day').valueOf();

        let adjustedDuration = 0;
        let newadjustedDuration = 0;
        // (recompile-touch)
        // True when the selected range contains ONLY holiday/excluded dates (no production
        // left after exclusions). Set inside the duration branches below, alongside the
        // existing newadjustedDuration fallback. Drives whether we send full vs reduced data.
        let isOnlyHolidayRange = false;

        const machineCountWithRunDuration = selectedMachines.length - data.machinesWithZeroRunDuration.length;

        // Create a set of machine-shift combos with zero runtime for quick lookup
        const zeroRuntimeSet = new Set();
        const selectedDeviceIds = deviceNameID
            .filter((d) => selectedMachines.includes(d.name))
            .map((d) => d.id);

        if (data.machineShiftZeroRuntimes) {
            for (const [deviceId, shifts] of data.machineShiftZeroRuntimes.entries()) {
                for (const { date, shift } of shifts) {
                    zeroRuntimeSet.add(`${deviceId}-${date}-${shift}`);
                }
            }
        }

        // Helper to check if a specific date should be excluded for a machine

        const isDateExceededFor20Hours = (deviceId, dateStr) => {
            // Production-day override always wins — never exclude a manually-set production day
            if (manualProductionSet.has(dateStr)) return false;
            if (data.dateRunExceeded && data.dateRunExceeded.has(deviceId)) {
                const excludedDates = data.dateRunExceeded.get(deviceId);
                return excludedDates.includes(dateStr);
            }
            return false;
        };

        // Helper: check if machine has zero run duration for a specific date
        const hasZeroRunForDate = (deviceId, dateStr) => {
            const key = `${deviceId}-${dateStr}`;
            const runDuration = data.machineRunByDate ? data.machineRunByDate.get(key) : undefined;
            return runDuration === 0;
        };

        {
            let _anyProd = false;
            let _d = startDate.startOf('day');
            const _end = endDate.startOf('day');
            const _sNos = selectedShift === "allshift"
                ? shifts.map(s => String(s.shift_no))
                : [String(selectedShift)];
            while (!_d.isAfter(_end) && !_anyProd) {
                const _ds = _d.format('YYYY-MM-DD');
                for (const _sNo of _sNos) {
                    if (_anyProd) break;
                    for (const _id of selectedDeviceIds) {
                        const _nm = gIdToName(_id);
                        if (isMachineProductionDur(_nm, _sNo, _ds) ||
                            (!isMachineHolidayDur(_nm, _sNo, _ds) && !isDateExceededFor20Hours(_id, _ds))) {
                            _anyProd = true;
                            break;
                        }
                    }
                }
                _d = _d.add(1, 'day');
            }
            isOnlyHolidayRange = !_anyProd;
        }

        if (cleanCustomerId(customerId) === window._env_.CUSTOMER_ID || cleanCustomerId(customerId) === window._env_.SMC_CUSTOMER_ID) {
            // Calculate duration1 and duration2 with breaktime removed across date range
            let duration1Adjusted = 0;
            let duration2Adjusted = 0;
            let duration2RemovedDueToCap = 0;
            let currentDay = startDate.startOf('day');
            const lastDay = endDate.startOf('day');

            const shiftsToProcess = selectedShift === "allshift"
                ? shifts.map(s => String(s.shift_no))
                : [String(selectedShift)];

            const dateWiseBreakdown = new Map(); // { date: { totalHours, removedHours, actualHours, excludedMachines } }
            let fullRawDuration1 = 0; // full shift time across all machines with no exclusions (for isOnlyHolidayRange fallback)

            while (!currentDay.isAfter(lastDay)) {
                const currentDateStr = currentDay.format('YYYY-MM-DD');

                const isManualHoliday = manualHolidaySet.has(currentDateStr);
                const isManualProduction = manualProductionSet.has(currentDateStr);

                if (!dateWiseBreakdown.has(currentDateStr)) {
                    dateWiseBreakdown.set(currentDateStr, {
                        totalHours: 0,
                        removedHours: 0,
                        actualHours: 0,
                        excludedMachines: []
                    });
                }
                const dateData = dateWiseBreakdown.get(currentDateStr);

                // Check if this date exceeds 20 hours for any selected machine
                const isDateExceededForAnyMachine = selectedDeviceIds.some(
                    deviceId => isDateExceededFor20Hours(deviceId, currentDateStr)
                );

                for (const shiftNo of shiftsToProcess) {
                    const shiftData = shifts.find(s => String(s.shift_no) === shiftNo);
                    if (!shiftData) continue;

                    const shiftTimes = getShiftTimes(shifts, shiftNo, currentDay);
                    const rawSecs = shiftTimes.from && shiftTimes.to
                        ? Math.floor((shiftTimes.to - shiftTimes.from) / 1000)
                        : 0;
                    const shiftSecsMinusBreak = Math.max(0, rawSecs - timeToSeconds(shiftData.break_time || "00:00:00"));
                    fullRawDuration1 += shiftSecsMinusBreak * selectedDeviceIds.length;

                    // Count machines per category for this shift/date. A manual holiday for a
                    let machinesWithRuntimeCount = 0;   // counted in duration2 (actual)
                    let duration1MachineCount = 0;       // counted in duration1 (target)
                    let machinesRemovedCount = 0;
                    let machineNamesIncluded = [];
                    let machineNamesRemoved = [];

                    for (const deviceId of selectedDeviceIds) {
                        const key = `${deviceId}-${currentDateStr}-${shiftNo}`;
                        const deviceName = gIdToName(deviceId);

                        // Manual holiday for this machine/shift → removed from everything
                        if (isMachineHolidayDur(deviceName, shiftNo, currentDateStr)) {
                            machinesRemovedCount++;
                            machineNamesRemoved.push(deviceName);
                            continue;
                        }

                        // Survives manual holiday → part of the target (duration1)
                        duration1MachineCount++;

                        // Production-override: always count in actual regardless of telemetry
                        if (isMachineProductionDur(deviceName, shiftNo, currentDateStr)) {
                            machinesWithRuntimeCount++;
                            machineNamesIncluded.push(deviceName);
                            continue;
                        }

                        // Auto 20h-threshold exclusion (affects duration2 only)
                        const isExcludedForDate = isDateExceededFor20Hours(deviceId, currentDateStr);

                        if (!zeroRuntimeSet.has(key)) {
                            if (!isExcludedForDate) {
                                machinesWithRuntimeCount++;
                                machineNamesIncluded.push(deviceName);
                            } else {
                                machinesRemovedCount++;
                                machineNamesRemoved.push(deviceName);
                            }
                        } else if (isExcludedForDate) {
                            machinesRemovedCount++;
                            machineNamesRemoved.push(deviceName);
                        }
                    }

                    // duration1 (target): machines NOT on manual holiday
                    const shiftDuration1 = shiftSecsMinusBreak * duration1MachineCount;
                    duration1Adjusted += shiftDuration1;
                    dateData.totalHours += shiftDuration1;

                    // duration2 (actual): machines with runtime, not threshold-excluded, not on holiday
                    const shiftDuration2Actual = shiftSecsMinusBreak * machinesWithRuntimeCount;
                    duration2Adjusted += shiftDuration2Actual;
                    dateData.actualHours += shiftDuration2Actual;
                    const shiftRemoved = Math.max(0, shiftDuration1 - shiftDuration2Actual);
                    duration2RemovedDueToCap += shiftRemoved;
                    dateData.removedHours += shiftRemoved;

                    if (machinesRemovedCount > 0) {
                        dateData.excludedMachines.push({
                            shift: shiftNo,
                            removedMachines: machineNamesRemoved,
                            includedMachines: machineNamesIncluded,
                            shiftHoursRemoved: (shiftRemoved / 3600).toFixed(2)
                        });
                    }
                }

                currentDay = currentDay.add(1, 'day');
            }

            adjustedDuration = duration1Adjusted;
            newadjustedDuration = duration2Adjusted;

            // Show excluded dates and their impact
            const excludedDatesList = [];
            dateWiseBreakdown.forEach((data, date) => {
                if (data.removedHours > 0) {
                    const removedHrs = parseFloat((data.removedHours / 3600).toFixed(2));
                    excludedDatesList.push({
                        'Excluded Date': date,
                        'Hours Removed': removedHrs.toFixed(2),
                        'From newadjustedDuration': removedHrs.toFixed(2) + ' hrs'
                    });
                }
            });
            if (excludedDatesList.length > 0) {
                const totalRemovedFromDuration = excludedDatesList.reduce((sum, item) => sum + parseFloat(item['Hours Removed']), 0);
            } else {
                console.log("No dates excluded - all target hours are included");
            }

            // Create table data
            const tableData = [];
            // Accumulate raw seconds to avoid rounding drift from .toFixed(2) per date
            let grandTotalSeconds1 = 0, grandRemovedSeconds1 = 0, grandActualSeconds1 = 0;

            // Get machines excluded for this date
            const getMachinesExcludedForDate = (dateStr) => {
                const excluded = [];
                for (const deviceId of selectedDeviceIds) {
                    if (isDateExceededFor20Hours(deviceId, dateStr)) {
                        excluded.push(deviceId);
                    }
                }
                return excluded;
            };

            dateWiseBreakdown.forEach((data, date) => {
                const totalHrs = parseFloat((data.totalHours / 3600).toFixed(2));
                const totalRemoved = parseFloat((data.removedHours / 3600).toFixed(2));
                const actualHrs = parseFloat((totalHrs - totalRemoved).toFixed(2));

                tableData.push({
                    Date: date,
                    'Total Hours': totalHrs.toFixed(2),
                    'Removed Hours': totalRemoved.toFixed(2),
                    'Actual Hours': actualHrs.toFixed(2),
                    'Formula': `${totalHrs.toFixed(2)} - ${totalRemoved.toFixed(2)} = ${actualHrs.toFixed(2)}`
                });

                grandTotalSeconds1 += data.totalHours;
                grandRemovedSeconds1 += data.removedHours;
                grandActualSeconds1 += (data.totalHours - data.removedHours);
            });

            const grandTotalHours = grandTotalSeconds1 / 3600;
            const grandRemovedHours = grandRemovedSeconds1 / 3600;
            const grandActualHours = grandActualSeconds1 / 3600;

            // Add total row to table
            tableData.push({
                Date: '🔵 TOTAL',
                'Total Hours': grandTotalHours.toFixed(2),
                'Removed Hours': grandRemovedHours.toFixed(2),
                'Actual Hours': grandActualHours.toFixed(2),
                'Formula': `${grandTotalHours.toFixed(2)} - ${grandRemovedHours.toFixed(2)} = ${grandActualHours.toFixed(2)}`
            });

            console.table(tableData);

            // Date-wise TARGET, REMOVED, ACTUAL breakdown
            const dateWiseDetailedBreakdown = [];
            dateWiseBreakdown.forEach((data, date) => {
                const targetHours = parseFloat((data.totalHours / 3600).toFixed(2));
                const removedHours = parseFloat((data.removedHours / 3600).toFixed(2));
                const actualHours = parseFloat(((data.totalHours - data.removedHours) / 3600).toFixed(2));
                const targetMinusRemoved = parseFloat((targetHours - removedHours).toFixed(2));

                dateWiseDetailedBreakdown.push({
                    'Date': date,
                    'Target': targetHours.toFixed(2),
                    'Removed': removedHours.toFixed(2),
                    'Actual': actualHours.toFixed(2),
                    'Formula': `${targetHours.toFixed(2)} - ${removedHours.toFixed(2)} = ${actualHours.toFixed(2)}`,
                    'Target - Removed': targetMinusRemoved.toFixed(2)
                });
            });

            // Add total row
            dateWiseDetailedBreakdown.push({
                'Date': '🔵 TOTAL',
                'Target': grandTotalHours.toFixed(2),
                'Removed': grandRemovedHours.toFixed(2),
                'Actual': grandActualHours.toFixed(2),
                'Formula': `${grandTotalHours.toFixed(2)} - ${grandRemovedHours.toFixed(2)} = ${grandActualHours.toFixed(2)}`,
                'Target - Removed': (grandTotalHours - grandRemovedHours).toFixed(2)
            });

            console.table(dateWiseDetailedBreakdown);

            // Use raw accumulated seconds — no rounding drift
            const beforeExclusionDuration = duration2Adjusted / 3600;
            newadjustedDuration = grandActualSeconds1;
            // When the range is entirely holidays, newadjustedDuration and/or adjustedDuration may be 0.
            // Fall back to full raw duration so Grafana ratios have a non-zero denominator.
            if (isOnlyHolidayRange && newadjustedDuration === 0) {
                const _fallback = adjustedDuration > 0 ? adjustedDuration : fullRawDuration1;
                adjustedDuration = _fallback;
                newadjustedDuration = _fallback;
            }
            const afterExclusionDuration = grandActualSeconds1 / 3600;
            const totalHoursRemovedFromDuration = beforeExclusionDuration - afterExclusionDuration;

            // Summary breakdown of removed hours by category
            const removedHoursSummary = new Map();
            dateWiseBreakdown.forEach((data, date) => {
                // data.removedHours already includes all removed hours
                const key = (data.removedHours / 3600).toFixed(2);
                removedHoursSummary.set(key, (removedHoursSummary.get(key) || 0) + 1);
            });

            const sortedSummary = Array.from(removedHoursSummary.entries()).sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));
            if (sortedSummary.length === 0 || (sortedSummary.length === 1 && sortedSummary[0][0] === '0.00')) {
                console.log(`%c  No machines removed (condition not met)`, "color: #4CAF50; font-weight: bold;");
            } else {
                sortedSummary.forEach(([hours, count]) => {
                    if (hours !== '0.00') {
                        console.log(`  %c${hours} hrs removed: ${count} date(s)`, "color: #FF5722; font-weight: bold;");
                    }
                });
            }

            // Detailed excluded machines by date with table format
            if (dateWiseBreakdown.size > 0) {
                const hasExclusions = Array.from(dateWiseBreakdown.values()).some(d => d.excludedMachines.length > 0);
                if (hasExclusions) {

                    // Build table data for removed shifts - show all shifts for excluded dates
                    const removedShiftsTable = [];

                    // Determine all shifts to show based on selection
                    const allShiftsToShow = selectedShift === "allshift"
                        ? shifts.map(s => String(s.shift_no))
                        : [String(selectedShift)];

                    // For each excluded date, show ALL shifts with all machines removed
                    dateWiseBreakdown.forEach((data, date) => {
                        if (data.removedHours > 0) {
                            const machineNames = selectedMachines.join(", ");
                            const hoursPerShift = (data.totalHours / allShiftsToShow.length / 3600).toFixed(2);

                            // Show ALL shifts for this date
                            allShiftsToShow.forEach(shiftNo => {
                                removedShiftsTable.push({
                                    'Date': date,
                                    'Shift': shiftNo,
                                    'Removed Machines': machineNames,
                                    'Included Machines': '—',
                                    'Hours Removed': hoursPerShift,
                                    'Reason': 'All machines excluded (Run <= 20% AND (Alarm+Idle+Disconnect > 20h))'
                                });
                            });
                        }
                    });

                    if (removedShiftsTable.length > 0) {

                        const dateWiseSummary = [];
                        dateWiseBreakdown.forEach((data, date) => {
                            if (data.excludedMachines.length > 0) {
                                const allRemovedPerDate = [];
                                const shiftsAffected = [];
                                data.excludedMachines.forEach(exc => {
                                    shiftsAffected.push(exc.shift);
                                    allRemovedPerDate.push(...exc.removedMachines);
                                });
                                const uniqueRemoved = [...new Set(allRemovedPerDate)];
                                dateWiseSummary.push({
                                    'Date': date,
                                    'Shifts': shiftsAffected.join(", "),
                                    'Removed': uniqueRemoved.join(", "),
                                    'All Machines': selectedMachines.join(", "),
                                    'Count': `${uniqueRemoved.length}/${selectedMachines.length}`
                                });
                            }
                        });
                        if (dateWiseSummary.length > 0) {
                            console.table(dateWiseSummary);
                        }


                        const masterTable = [];
                        dateWiseBreakdown.forEach((data, date) => {
                            if (data.excludedMachines.length > 0) {
                                data.excludedMachines.forEach((exc, idx) => {
                                    masterTable.push({
                                        'Date': date,
                                        'Shift': exc.shift,
                                        'Removed Machines': exc.removedMachines.join(", "),
                                        'Included Machines': exc.includedMachines?.length > 0 ? exc.includedMachines.join(", ") : '—',
                                        'Hours Removed': `${exc.shiftHoursRemoved} hrs`,
                                        'Reason': 'Run <= 20% & (Alarm + Idle + Disconnect > 20h)'
                                    });
                                });
                            }
                        });
                        if (masterTable.length > 0) {
                            console.table(masterTable);
                        }
                    }
                } else {
                    console.log("\n");
                    console.log("%c✅ NO MACHINES EXCLUDED (All machines meet the condition criteria)", "color: #4CAF50; font-weight: bold; font-size: 12px;");
                }
            }

        } else {
            // Calculate duration1 and duration2 without breaktime removal across date range
            let duration1Adjusted = 0;
            let duration2Adjusted = 0;
            let duration2RemovedDueToCap = 0;
            let currentDay = startDate.startOf('day');
            const lastDay = endDate.startOf('day');

            const shiftsToProcess = selectedShift === "allshift"
                ? shifts.map(s => String(s.shift_no))
                : [String(selectedShift)];

            const dateWiseBreakdown = new Map(); // { date: { totalHours, removedHours, actualHours, excludedMachines } }
            let fullRawDuration2 = 0; // full shift time across all machines with no exclusions (for isOnlyHolidayRange fallback)

            while (!currentDay.isAfter(lastDay)) {
                const currentDateStr = currentDay.format('YYYY-MM-DD');

                const isManualHoliday = manualHolidaySet.has(currentDateStr);
                const isManualProduction = manualProductionSet.has(currentDateStr);

                // Initialize date entry (needed even on holiday dates)
                if (!dateWiseBreakdown.has(currentDateStr)) {
                    dateWiseBreakdown.set(currentDateStr, {
                        totalHours: 0,
                        removedHours: 0,
                        actualHours: 0,
                        excludedMachines: []
                    });
                }
                const dateData = dateWiseBreakdown.get(currentDateStr);

                for (const shiftNo of shiftsToProcess) {
                    const shiftTimes = getShiftTimes(shifts, shiftNo, currentDay);
                    const shiftSecs = shiftTimes.from && shiftTimes.to
                        ? Math.floor((shiftTimes.to - shiftTimes.from) / 1000)
                        : 0;
                    fullRawDuration2 += shiftSecs * selectedDeviceIds.length;

                    // Count machines per category for this shift/date. A manual holiday for a
                    // machine removes it from BOTH duration1 (target) and duration2 (actual);
                    // the auto 20h-threshold removes it from duration2 only.
                    let machinesWithRuntimeCount = 0;   // counted in duration2 (actual)
                    let duration1MachineCount = 0;       // counted in duration1 (target)
                    let machinesRemovedCount = 0;
                    let machineNamesIncluded = [];
                    let machineNamesRemoved = [];

                    for (const deviceId of selectedDeviceIds) {
                        const key = `${deviceId}-${currentDateStr}-${shiftNo}`;
                        const deviceName = gIdToName(deviceId);

                        // Manual holiday for this machine/shift → removed from everything
                        if (isMachineHolidayDur(deviceName, shiftNo, currentDateStr)) {
                            machinesRemovedCount++;
                            machineNamesRemoved.push(deviceName);
                            continue;
                        }

                        // Survives manual holiday → part of the target (duration1)
                        duration1MachineCount++;

                        // Production-override: always count in actual regardless of telemetry
                        if (isMachineProductionDur(deviceName, shiftNo, currentDateStr)) {
                            machinesWithRuntimeCount++;
                            machineNamesIncluded.push(deviceName);
                        } else if (!zeroRuntimeSet.has(key) &&
                            !isDateExceededFor20Hours(deviceId, currentDateStr)) {
                            machinesWithRuntimeCount++;
                            machineNamesIncluded.push(deviceName);
                        } else {
                            machinesRemovedCount++;
                            machineNamesRemoved.push(deviceName);
                        }
                    }

                    // duration1 (target): machines NOT on manual holiday
                    const shiftDuration1 = shiftSecs * duration1MachineCount;
                    duration1Adjusted += shiftDuration1;
                    dateData.totalHours += shiftDuration1;

                    // duration2 (actual): machines with runtime, not threshold-excluded, not on holiday
                    const shiftDuration2Actual = shiftSecs * machinesWithRuntimeCount;
                    duration2Adjusted += shiftDuration2Actual;
                    dateData.actualHours += shiftDuration2Actual;
                    const shiftRemoved = Math.max(0, shiftDuration1 - shiftDuration2Actual);
                    duration2RemovedDueToCap += shiftRemoved;
                    dateData.removedHours += shiftRemoved;

                    if (machinesRemovedCount > 0) {
                        dateData.excludedMachines.push({
                            shift: shiftNo,
                            removedMachines: machineNamesRemoved,
                            includedMachines: machineNamesIncluded,
                            shiftHoursRemoved: (shiftRemoved / 3600).toFixed(2)
                        });
                    }
                }

                currentDay = currentDay.add(1, 'day');
            }

            adjustedDuration = duration1Adjusted;
            newadjustedDuration = duration2Adjusted;

            // Create table data
            const tableData = [];
            // Accumulate raw seconds to avoid rounding drift from .toFixed(2) per date
            let grandTotalSeconds = 0, grandRemovedSeconds = 0, grandActualSeconds = 0;

            // Get machines excluded for this date
            const getMachinesExcludedForDate = (dateStr) => {
                const excluded = [];
                for (const deviceId of selectedDeviceIds) {
                    if (isDateExceededFor20Hours(deviceId, dateStr)) {
                        excluded.push(deviceId);
                    }
                }
                return excluded;
            };

            dateWiseBreakdown.forEach((data, date) => {
                const totalHrs = parseFloat((data.totalHours / 3600).toFixed(2));
                const actualHrs = parseFloat((data.actualHours / 3600).toFixed(2));
                const totalRemoved = parseFloat(((data.totalHours - data.actualHours) / 3600).toFixed(2));

                tableData.push({
                    Date: date,
                    'Total Hours': totalHrs.toFixed(2),
                    'Removed Hours': totalRemoved.toFixed(2),
                    'Actual Hours': actualHrs.toFixed(2),
                    'Formula': `${totalHrs.toFixed(2)} - ${totalRemoved.toFixed(2)} = ${actualHrs.toFixed(2)}`
                });

                // Accumulate raw seconds (not rounded hours) to preserve precision
                grandTotalSeconds += data.totalHours;
                grandRemovedSeconds += (data.totalHours - data.actualHours);
                grandActualSeconds += data.actualHours;
            });

            // Derive display-only hour values from the raw second totals
            const grandTotalHours = grandTotalSeconds / 3600;
            const grandRemovedHours = grandRemovedSeconds / 3600;
            const grandActualHours = grandActualSeconds / 3600;

            // Add total row to table
            tableData.push({
                Date: '🔵 TOTAL',
                'Total Hours': grandTotalHours.toFixed(2),
                'Removed Hours': grandRemovedHours.toFixed(2),
                'Actual Hours': grandActualHours.toFixed(2),
                'Formula': `${grandTotalHours.toFixed(2)} - ${grandRemovedHours.toFixed(2)} = ${grandActualHours.toFixed(2)}`
            });

            // Date-wise TARGET, REMOVED, ACTUAL breakdown
            const dateWiseDetailedBreakdown = [];
            dateWiseBreakdown.forEach((data, date) => {
                const targetHours = parseFloat((data.totalHours / 3600).toFixed(2));
                const removedHours = parseFloat(((data.totalHours - data.actualHours) / 3600).toFixed(2));
                const actualHours = parseFloat((data.actualHours / 3600).toFixed(2));
                const machinesExcluded = (data.dateRunExceeded?.has(date)) ? 'YES' : 'NO';

                dateWiseDetailedBreakdown.push({
                    'Date': date,
                    'Target Hours': targetHours.toFixed(2),
                    'Removed Hours': removedHours.toFixed(2),
                    'Actual Hours': actualHours.toFixed(2),
                    'Excluded': machinesExcluded,
                    'Percentage Used': removedHours > 0 ? ((actualHours / targetHours) * 100).toFixed(1) + '%' : '100%'
                });
            });

            // Use raw accumulated seconds — no rounding drift
            newadjustedDuration = grandActualSeconds;
            // When the range is entirely holidays, newadjustedDuration and/or adjustedDuration may be 0.
            // Fall back to full raw duration so Grafana ratios have a non-zero denominator.
            if (isOnlyHolidayRange && newadjustedDuration === 0) {
                const _fallback = adjustedDuration > 0 ? adjustedDuration : fullRawDuration2;
                adjustedDuration = _fallback;
                newadjustedDuration = _fallback;
            }

            // Summary breakdown of removed hours by category
            const removedHoursSummary = new Map();
            dateWiseBreakdown.forEach((data, date) => {
                // data.totalHours - data.actualHours gives total removed (both zero-runtime and AND condition)
                const key = ((data.totalHours - data.actualHours) / 3600).toFixed(2);
                removedHoursSummary.set(key, (removedHoursSummary.get(key) || 0) + 1);
            });

            const sortedSummary = Array.from(removedHoursSummary.entries()).sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));
            sortedSummary.forEach(([hours, count]) => {
                console.log(`  ${hours} hrs removed: ${count} date(s)`);
            });

            // Detailed excluded machines by date with table format
            if (dateWiseBreakdown.size > 0) {
                const hasExclusions = Array.from(dateWiseBreakdown.values()).some(d => d.excludedMachines.length > 0);
                if (hasExclusions) {

                    // Build table data for removed shifts - show all shifts for excluded dates
                    const removedShiftsTable = [];

                    // Determine all shifts to show based on selection
                    const allShiftsToShow = selectedShift === "allshift"
                        ? shifts.map(s => String(s.shift_no))
                        : [String(selectedShift)];


                    // For each excluded date, show ALL shifts with all machines removed
                    dateWiseBreakdown.forEach((data, date) => {
                        if (data.removedHours > 0) {
                            const machineNames = selectedMachines.join(", ");
                            const hoursPerShift = (data.totalHours / allShiftsToShow.length / 3600).toFixed(2);

                            // Show ALL shifts for this date
                            allShiftsToShow.forEach(shiftNo => {
                                removedShiftsTable.push({
                                    'Date': date,
                                    'Shift': shiftNo,
                                    'Removed Machines': machineNames,
                                    'Included Machines': '—',
                                    'Hours Removed': hoursPerShift,
                                    'Reason': 'All machines excluded (Run <= 20% AND (Alarm+Idle+Disconnect > 20h))'
                                });
                            });
                        }
                    });

                    if (removedShiftsTable.length > 0) {

                        const dateWiseSummary = [];
                        dateWiseBreakdown.forEach((data, date) => {
                            if (data.excludedMachines.length > 0) {
                                const allRemovedPerDate = [];
                                const shiftsAffected = [];
                                data.excludedMachines.forEach(exc => {
                                    shiftsAffected.push(exc.shift);
                                    allRemovedPerDate.push(...exc.removedMachines);
                                });
                                const uniqueRemoved = [...new Set(allRemovedPerDate)];
                                dateWiseSummary.push({
                                    'Date': date,
                                    'Shifts': shiftsAffected.join(", "),
                                    'Removed': uniqueRemoved.join(", "),
                                    'All Machines': selectedMachines.join(", "),
                                    'Count': `${uniqueRemoved.length}/${selectedMachines.length}`
                                });
                            }
                        });
                        if (dateWiseSummary.length > 0) {
                            console.table(dateWiseSummary);
                        }
                        // Master consolidated table with all details
                        const masterTable = [];
                        dateWiseBreakdown.forEach((data, date) => {
                            if (data.excludedMachines.length > 0) {
                                data.excludedMachines.forEach((exc, idx) => {
                                    masterTable.push({
                                        'Date': date,
                                        'Shift': exc.shift,
                                        'Removed Machines': exc.removedMachines.join(", "),
                                        'Included Machines': exc.includedMachines?.length > 0 ? exc.includedMachines.join(", ") : '—',
                                        'Hours Removed': `${exc.shiftHoursRemoved} hrs`,
                                        'Reason': 'Run <= 20% & (Alarm + Idle + Disconnect > 20h)'
                                    });
                                });
                            }
                        });
                        if (masterTable.length > 0) {
                            console.table(masterTable);
                        }
                    }

                } else {
                    console.log("\n");
                    console.log("%c✅ NO MACHINES EXCLUDED (All machines meet the condition criteria)", "color: #4CAF50; font-weight: bold; font-size: 12px;");
                }
            }
        }

        const baseUrl = window._env_?.SERVER_URL || '';
        const GRAFANA_URL = window._env_?.GRAFANA_URL || '';

        // Calculate total days in range minus manual holidays
        const totalDaysInRange = endDate.startOf('day').diff(startDate.startOf('day'), 'day') + 1;
        const manualHolidayCount = manualHolidaySet.size;
        const effectiveDays = Math.max(0, totalDaysInRange - manualHolidayCount);

        // isOnlyHolidayRange was determined above (alongside the newadjustedDuration fallback):
        // - Only holiday/excluded dates → send full (unreduced) data, since reducing zeros everything.
        // - Holiday + production        → keep the reduced new_total_* values (holiday data subtracted).
        // When the range is entirely holidays, fall back to the raw totals so the
        // dashboard still shows the holiday-day data instead of zeros.
        const effectiveDurations = isOnlyHolidayRange
            ? {
                ...data.durations,
                new_total_run_duration: data.durations.total_run_duration,
                new_total_idle_duration: data.durations.total_idle_duration,
                new_total_alarm_duration: data.durations.total_alarm_duration,
                new_total_disconnect_duration: data.durations.total_disconnect_duration,
            }
            : data.durations;

        const grafanaData = {
            oee: oeeVar,
            duration: {
                ...effectiveDurations,
                // Break-window run/idle/alarm/disconnect (seconds), grouped together
                total_duration: {
                    break_run: data.durations?.break_run || 0,
                    break_idle: data.durations?.break_idle || 0,
                    break_alarm: data.durations?.break_alarm || 0,
                    break_disconnect: data.durations?.break_disconnect || 0,
                },
            },
            machinePerformance: data.machinePerformance,
            manualHolidayDays: manualHolidayCount,
            effectiveDays,
        };


        if (newadjustedDuration < adjustedDuration) {
            const diff = (adjustedDuration - newadjustedDuration) / 3600;
        }
        const fromDateStr = startDate.format("YYYY-MM-DD");
        const toDateStr = endDate.format("YYYY-MM-DD");
        const shiftParam =
            selectedShift && selectedShift !== "allshift"
                ? selectedShift
                : shifts.map(s => s.shift_no).join(",");
        const encodedData = encodeURIComponent(JSON.stringify(grafanaData));
        const encodedMetricByPeriod = encodeURIComponent(JSON.stringify(data.metricByPeriod || null));
        const reporturl = `${window._env_.SERVER_URL2}report/idle_report/${machineParam}/${shiftParam}/${fromDateStr}/${toDateStr}/1/10000000000000`;
        console.log(reporturl, 'reporturl');

        const oeeReportUrl =
            cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID
                ? `${window._env_.SERVER_URL2}report/oee_report/${machineParam}/${shiftParam}/${fromDateStr}/${toDateStr}/1/10000`
                : `${window._env_.SERVER_URL2}report/oee_report/${selectedMachines[0]}/${shiftParam}/${fromDateStr}/${toDateStr}/1/10`;
        console.log(oeeReportUrl, 'oeeReportUrl');

        const roundedAdjustedDuration = Math.round(adjustedDuration / 60) * 60;
        const roundedNewadjustedDuration = Math.round(newadjustedDuration / 60) * 60;
        const mainUrl = `${GRAFANA_URL}d/cfa1esd5995a8b/one-page-dashboard-main-2?orgId=1&var-token=${bearerToken}&var-customerid=${cleanedId}&var-entityType=${entityType}&var-entityId=${entityId}&from=${from}&to=${to}&var-duration=${roundedAdjustedDuration}&var-duration1=${roundedNewadjustedDuration}&var-url=${baseUrl}&var-idleReasonReportUrl=${reporturl}&var-oeeReportUrl=${oeeReportUrl}&var-isAllMachinesSelected=${isAllMachinesSelected}&var-grafanaurl=${GRAFANA_URL}&var-shifts=${shiftVar}&var-data=${encodedData}&var-selectedMachines=${encodeURIComponent(machineParam)}&var-apidata=${encodedMetricByPeriod}&kiosk&theme=light&refresh=5s`;

        return { mainUrl };
    }, [customerId, token, selectedDevice, selectedShift, shifts, startDate, endDate, devices, getShiftTimes, selectedMachines]);

    // Initial data fetching
    useEffect(() => {
        if (customerId) {
            Promise.all([fetchShifts(), fetchDevices()]);
        }
    }, [customerId, fetchShifts, fetchDevices]);


    useEffect(() => {
        if (shifts.length === 0 || selectedShift !== null) return;

        setSelectedShift(getCurrentShift(shifts));

        // If the current time is before the first shift of the day starts,
        // default both date pickers to yesterday so the user sees real data
        const firstShift = [...shifts].sort((a, b) => Number(a.shift_no) - Number(b.shift_no))[0];
        if (firstShift) {
            const [h, m] = firstShift.start_time.split(':').map(Number);
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            const firstShiftMinutes = h * 60 + m;
            if (currentMinutes < firstShiftMinutes) {
                const yesterday = dayjs().subtract(1, 'day');
                setStartDate(yesterday);
                setEndDate(yesterday);
            }
        }
    }, [shifts, selectedShift, getCurrentShift]);

    // If the currently selected shift is a future shift (e.g. user switched date to today),
    // auto-reset to the current shift to avoid a blank screen.
    useEffect(() => {
        if (!shifts.length || !selectedShift || selectedShift === 'allshift') return;
        const isToday = startDate.isSame(dayjs(), 'day');
        if (!isToday) return;
        const times = getShiftTimes(shifts, selectedShift, startDate);
        if (times.from !== null && times.from > Date.now()) {
            setSelectedShift(getCurrentShift(shifts));
        }
    }, [startDate, shifts, selectedShift, getShiftTimes, getCurrentShift]);

    // Handle submit with all data fetching
    const handleSubmit = useCallback(async () => {
        // Without a live tenant token the embedded URL's var-token is empty and
        // ThingsBoard/Grafana silently falls back to its own default dashboard —
        // refuse to build/set the URL in that case instead of showing the wrong screen.
        if (!localStorage.getItem('token1')) {
            setSubmitError('Session is refreshing — please click Submit again in a moment.');
            return;
        }
        setSubmitError('');
        const myRequestId = ++requestIdRef.current;
        setIsLoading(true);
        try {
            // Refresh manual holiday/production overrides before computing.
            await fetchManualOverrides();
            const data = await fetchAllDashboardData();
            // Discard this result if a newer submit has started in the meantime.
            if (data && myRequestId === requestIdRef.current) {
                const { mainUrl } = generateGrafanaUrls(data);
                setMainGrafanaUrl(mainUrl);
            }
        } finally {
            if (myRequestId === requestIdRef.current) setIsLoading(false);
        }
    }, [fetchManualOverrides, fetchAllDashboardData, generateGrafanaUrls]);

    useEffect(() => {
        const currentShift = getCurrentShift(shifts);
        const isCurrentShift = currentShift === selectedShift;
        const isEndDateToday = endDate.isSame(dayjs(), "day");
        let intervalId = null;
        if (isEndDateToday && (isCurrentShift || selectedShift === "allshift")) {
            intervalId = setInterval(() => {
                if (document.visibilityState === "visible") handleSubmit();
            }, 60 * 5000);
        }
        return () => clearInterval(intervalId);
    }, [handleSubmit, selectedShift, endDate, shifts, getCurrentShift]);

    useEffect(() => {
        const readyToFetch =
            customerId &&
            localStorage.getItem('token1') && // wait for tenant token, else iframe loads TB default dashboard
            selectedShift !== null &&
            shifts.length > 0 &&
            selectedMachines.length > 0 &&
            deviceNameID.length > 0 &&
            devices.length > 0 &&
            startDate &&
            endDate;

        if (readyToFetch && isInitialLoad.current) {
            handleSubmit();
            isInitialLoad.current = false;
        }
    }, [
        customerId,
        token,
        selectedShift,
        shifts,
        selectedMachines,
        deviceNameID,
        devices,
        startDate,
        endDate,
        handleSubmit
    ]);


    // Memoized device options
    const deviceOptions = useMemo(() => [
        <MenuItem key="all" value="all">All Machines</MenuItem>,
        ...devices.map((d) => (
            <MenuItem key={d.id.id} value={d.id.id}>
                {d.name}
            </MenuItem>
        ))
    ], [devices]);

    // Drive isBeforeFirstShift state: set it on shift load and schedule a timeout
    // that fires the exact moment the first shift starts — no page refresh needed.
    useEffect(() => {
        if (!shifts.length) return;
        const firstShift = [...shifts].sort((a, b) => Number(a.shift_no) - Number(b.shift_no))[0];
        if (!firstShift) return;

        const [h, m] = firstShift.start_time.split(':').map(Number);
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const firstShiftMinutes = h * 60 + m;

        if (currentMinutes < firstShiftMinutes) {
            setIsBeforeFirstShift(true);

            // ms remaining until first shift starts (account for seconds/ms already elapsed)
            const msUntilStart =
                ((firstShiftMinutes - currentMinutes) * 60 - now.getSeconds()) * 1000
                - now.getMilliseconds();

            const timer = setTimeout(() => {
                setIsBeforeFirstShift(false);
                setStartDate(dayjs());
                setEndDate(dayjs());
                setSelectedShift(String(firstShift.shift_no));
            }, msUntilStart);

            return () => clearTimeout(timer);
        } else {
            setIsBeforeFirstShift(false);
        }
    }, [shifts]);

    // Memoized shift options — disable shifts that haven't started yet (only on today's date)
    const shiftOptions = useMemo(() => {
        const now = Date.now();
        const isToday = startDate.isSame(dayjs(), 'day');

        return [
            <MenuItem key="allshift" value="allshift">All Shifts</MenuItem>,
            ...shifts.map((shift) => {
                const isFuture = isToday && (() => {
                    const times = getShiftTimes(shifts, String(shift.shift_no), startDate);
                    return times.from !== null && times.from > now;
                })();
                return (
                    <MenuItem key={shift.shift_no} value={String(shift.shift_no)} disabled={isFuture}>
                        {shift.shift_no}{isFuture ? ' (upcoming)' : ''}
                    </MenuItem>
                );
            })
        ];
    }, [shifts, startDate, getShiftTimes]);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 65px)',
            background: '#fefcfcff',
            boxSizing: 'border-box',
        }}>
            <div style={{
                padding: '20px 30px',
                borderBottom: '1px solid #e0e0e0',
                background: '#ffffff',
                flexShrink: 0,
                marginTop: '30px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <h4 style={{ margin: 0 }}><b>KPI Dashboard</b></h4>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        alignItems: 'center'
                    }}>
                        {showPlantFilter && (
                            <PlantFilterSelect plantOptions={plantOptions} selectedPlants={selectedPlants} onChange={handlePlantChange} />
                        )}
                        {showMachineGroupsDropdown && (
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel sx={{ fontSize: '13px', color: '#86868b' }}>Machine Group</InputLabel>
                                <Select
                                    multiple
                                    value={selectedGroups}
                                    onChange={(e) => handleGroupChange(e.target.value)}
                                    label="Machine Group"
                                    renderValue={(selected) => {
                                        if (selected.length === machineGroups.length) return "All Groups";
                                        if (selected.length === 0) return "Select Groups";
                                        return selected.slice(0, 2).join(", ") + (selected.length > 2 ? "..." : "");
                                    }}
                                    sx={{
                                        fontSize: '14px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '10px',
                                            border: '1px solid rgba(0, 0, 0, 0.12)',
                                            '&:hover': {
                                                borderColor: '#007AFF',
                                            },
                                            '&.Mui-focused': {
                                                borderColor: '#007AFF',
                                                boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.1)',
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="all">
                                        <Checkbox checked={selectedGroups.length === machineGroups.length} />
                                        <ListItemText primary="All" />
                                    </MenuItem>
                                    {machineGroups.map((g) => (
                                        <MenuItem key={g.name} value={g.name}>
                                            <Checkbox checked={selectedGroups.includes(g.name)} />
                                            <ListItemText primary={g.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel sx={{ fontSize: '13px', color: '#86868b' }}>Machines</InputLabel>
                            <Select
                                multiple
                                value={selectedMachines}
                                onChange={(e) => handleMachineChange(e.target.value)}
                                onClose={() => setMachineDropdownSearch("")}
                                label="Machines"
                                renderValue={(selected) =>
                                    isAllMachinesSelected ? "All Machines" :
                                        selected.slice(0, 1).join(", ") + (selected.length > 1 ? "..." : "")
                                }
                                MenuProps={{
                                    autoFocus: false,
                                    PaperProps: { style: { maxHeight: 320 } },
                                }}
                                sx={{
                                    fontSize: '14px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        border: '1px solid rgba(0, 0, 0, 0.12)',
                                        '&:hover': {
                                            borderColor: '#007AFF',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#007AFF',
                                            boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.1)',
                                        }
                                    }
                                }}
                            >
                                <ListSubheader sx={{ p: 0 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        autoFocus
                                        placeholder="Search machines..."
                                        value={machineDropdownSearch}
                                        onChange={(e) => setMachineDropdownSearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key !== "Escape") e.stopPropagation();
                                        }}
                                        sx={{ p: 0.5, pl: 1.5 }}
                                        InputProps={{
                                            sx: { fontSize: "0.8rem" },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </ListSubheader>
                                <MenuItem value="all">
                                    <Checkbox checked={isAllMachinesSelected} />
                                    <ListItemText primary="All Machines" />
                                </MenuItem>
                                {availableMachines
                                    .filter((machine) =>
                                        machine.toLowerCase().includes(machineDropdownSearch.toLowerCase())
                                    )
                                    .map((machine) => (
                                        <MenuItem key={machine} value={machine}>
                                            <Checkbox checked={selectedMachines.includes(machine)} />
                                            <ListItemText primary={machine} />
                                        </MenuItem>
                                    ))}
                                {availableMachines.filter((machine) =>
                                    machine.toLowerCase().includes(machineDropdownSearch.toLowerCase())
                                ).length === 0 && (
                                    <MenuItem disabled>
                                        <ListItemText primary="No machines found" />
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Shift *</InputLabel>
                            <Select
                                value={selectedShift || ''}
                                onChange={(e) => setSelectedShift(e.target.value)}
                                label="Shift"
                                disabled={shifts.length === 0}
                            >
                                {shiftOptions}
                            </Select>
                        </FormControl>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(val) => {
                                    setStartDate(val);
                                    // Clamp end date: must not exceed 1 month from new start or today
                                    const maxAllowed = val.add(1, 'month').isAfter(dayjs()) ? dayjs() : val.add(1, 'month');
                                    if (endDate.isAfter(maxAllowed)) {
                                        setEndDate(maxAllowed);
                                    }
                                }}
                                format="DD-MM-YYYY"
                                maxDate={dayjs()}
                                shouldDisableDate={(date) => isBeforeFirstShift && date.isSame(dayjs(), 'day')}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { minWidth: 160 }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(val) => setEndDate(val)}
                                format="DD-MM-YYYY"
                                minDate={startDate}
                                maxDate={startDate.add(1, 'month').isAfter(dayjs()) ? dayjs() : startDate.add(1, 'month')}
                                shouldDisableDate={(date) => isBeforeFirstShift && date.isSame(dayjs(), 'day')}
                                slotProps={{
                                    textField: {
                                        size: "small",
                                        sx: { minWidth: 160 }
                                    }
                                }}
                            />
                        </LocalizationProvider>


                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isLoading || selectedMachines.length === 0}
                            sx={{
                                minWidth: 120,
                                height: 40,
                                background: '#f47803ff',
                                '&:hover': { background: '#e06d00ff' },
                                '&.Mui-disabled': { background: '#f4780380' },
                                textTransform: 'none',
                                fontWeight: 'bold',
                                gap: '8px'
                            }}
                        >
                            {isLoading && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                            {isLoading ? 'Loading...' : 'Submit'}
                        </Button>
                    </div>
                </div>
                {submitError && (
                    <div style={{ color: '#c0392b', fontSize: '13px', marginTop: '6px' }}>
                        {submitError}
                    </div>
                )}
            </div>

            <div style={{
                flex: 1,
                overflow: 'hidden',
                background: '#fff',
                position: 'relative',
            }}>
                {isLoading ? (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '16px',
                        background: '#f8f9fa',
                    }}>
                        <CircularProgress size={48} sx={{ color: '#f47803' }} />
                        <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: 500 }}>
                            Fetching dashboard data...
                        </span>
                    </div>
                ) : mainGrafanaUrl ? (
                    <>
                        <iframe
                            src={mainGrafanaUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                display: 'block'
                            }}
                            title="Grafana KPI Dashboard - Main"
                            allowFullScreen
                        />
                        <div
                            style={{
                                position: 'absolute',
                                top: 2,
                                right: 14,
                                width: 80,
                                height: "100%",
                                backgroundColor: 'transparent',
                                zIndex: 10
                            }}
                        />
                        <div //oee %
                            style={{
                                position: 'absolute',
                                top: 0,
                                width: "100%",
                                height: 40,
                                backgroundColor: 'transparent',
                                zIndex: 10,
                            }}
                        />
                        <div //oee %
                            style={{
                                position: 'absolute',
                                top: 100,
                                width: "100%",
                                height: 40,
                                backgroundColor: 'transparent',
                                zIndex: 10,
                            }}
                        />
                        <div //oee %
                            style={{
                                position: 'absolute',
                                top: 400,
                                width: "100%",
                                height: 40,
                                backgroundColor: 'transparent',
                                zIndex: 10,
                            }}
                        />
                    </>
                ) : (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        background: '#f8f9fa',
                        color: '#6c757d',
                        fontSize: '14px'
                    }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
                        </svg>
                        {submitError || 'Select filters and click Submit to load the dashboard'}
                    </div>
                )}
            </div>
        </div>
    );
}